import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Check, AlertTriangle, Upload, X, Home, BedDouble, Pencil } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { useRazorpayScript, RAZORPAY_BLOCKED_MESSAGE } from "../utils/razorpay";
import { shrinkPhoto, readJson } from "../utils/uploadPhotos";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

export default function GuestFormPage() {
  const { token }      = useParams();
  const { hotelName, guestPolicyText, idProofUploadEnabled } = useSettings();
  const [loading, setLoading]     = useState(true);
  const [notFound, setNotFound]   = useState(false);
  const [booking, setBooking]     = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState("");
  const [idFiles, setIdFiles]     = useState([]);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  /* A channel-manager booking already carries the guest's name and contact,
     so there is nothing left to ask for but the identity document. The
     details are still SHOWN, and still editable on request: an OTA masks
     what it passes on — Airbnb sends a first name and an initial and no
     email at all, Booking.com a relay alias — so only the guest can tell us
     whether what we hold is usable. */
  const [editDetails, setEditDetails] = useState(false);
  const [paying, setPaying]       = useState(false);
  const [payError, setPayError]   = useState("");
  const { ready: razorpayReady, failed: razorpayFailed } = useRazorpayScript(!!booking?.payments?.enabled);

  const [form, setForm] = useState({
    numberOfGuests: 1,
    fullNames: [""],
    travelFrom: "",
    travelTo: "",
    purposeOfVisit: "",
    whatsapp: "",
    email: "",
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Every guest needs a name, and — with the ID upload on — a document each.
  // At most 10 documents fit in one submission (the server's limit).
  const guestTotal = Math.max(1, Number(form.numberOfGuests) || 1);
  const namedGuests = form.fullNames.filter(n => n?.trim()).length;
  const idsNeeded = Math.min(guestTotal, 10);
  const idsOnFile = booking?.idProofCount || 0;
  const idsCovered = idsOnFile >= idsNeeded;

  // Nothing to ask for but the document: we already hold a name for every
  // guest and a way to reach them, and the guest hasn't asked to change any
  // of it. An OTA usually passes only the lead guest's name, so for a party
  // of two or more the name fields open.
  const knownOnly = !!booking?.detailsOnFile && !editDetails && namedGuests >= guestTotal;

  useEffect(() => {
    fetch(`${API}/guest-form/${token}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(j => {
        setBooking(j.data);
        setSubmitted(!!j.data.alreadySubmitted);
        const known = j.data.guest || {};
        const count = known.numberOfGuests || j.data.guestCount || f.numberOfGuests;
        const knownNames = known.fullNames?.length ? known.fullNames : f.fullNames;
        setForm(f => ({
          ...f,
          numberOfGuests: count,
          // One field per guest, the names we already hold filled in.
          fullNames:      Array.from({ length: Math.max(1, count) }, (_, i) => knownNames[i] || ""),
          whatsapp:       known.whatsapp       || f.whatsapp,
          email:          known.email          || j.data.guestEmail || f.email,
          travelFrom:     known.travelFrom     || f.travelFrom,
          travelTo:       known.travelTo       || f.travelTo,
          purposeOfVisit: known.purposeOfVisit || f.purposeOfVisit,
        }));
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  // Photos are shrunk before upload (PDFs go as they are), so an ID for
  // every guest still fits in one submission.
  async function addFile(e) {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    // One at a time, in order — a phone names every photo "image.jpg", and
    // several full-size decodes at once can exhaust its memory.
    const files = [];
    for (const f of picked) files.push(f.type.startsWith("image/") ? (await shrinkPhoto(f)).file : f);
    setIdFiles(prev => [...prev, ...files].slice(0, 10));
  }
  function removeFile(i) {
    setIdFiles(prev => prev.filter((_, j) => j !== i));
  }

  async function submit() {
    setError("");
    /* Only vouch for the details when the guest is actually entering them.
       In the ID-only flow the server falls back to what the channel manager
       already gave us, so demanding a re-typed name here would defeat the
       point. Airbnb passes no phone number, so that one is still required
       even then — hence the check on the value rather than on the mode. */
    if (namedGuests < guestTotal) {
      setError(`Please enter the full name of all ${guestTotal} guests.`);
      return;
    }
    if (!form.whatsapp.trim())      { setError("WhatsApp number is required."); return; }
    const waDigits = form.whatsapp.replace(/\D/g, "");
    if (waDigits.length < 7 || waDigits.length > 15) { setError("Enter a valid WhatsApp number (digits only, with country code if needed)."); return; }
    if (guestPolicyText && !booking?.policyAlreadyAccepted && !policyAccepted) {
      setError("Please read and accept the Guest Policy & Terms and Conditions before continuing.");
      return;
    }
    // One ID per guest when the tenant has the upload switched on, unless
    // that many are already on file — the link can be reopened to correct a
    // detail, and that shouldn't demand uploading ID a second time. A new
    // upload replaces what's on file, so it has to cover everyone.
    if (idProofUploadEnabled && (idFiles.length ? idFiles.length < idsNeeded : !idsCovered)) {
      setError(`Please upload an identity proof for each guest — ${idsNeeded} needed, ${idFiles.length} added.`);
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("whatsapp", form.whatsapp);
      fd.append("policyAccepted", policyAccepted ? "true" : "false");
      /* Post only what the guest actually filled in. A field left out is a
         field the server leaves exactly as the channel manager set it —
         sending empty strings would blank real data. */
      if (!knownOnly) {
        fd.append("numberOfGuests", form.numberOfGuests);
        fd.append("travelFrom", form.travelFrom);
        fd.append("travelTo", form.travelTo);
        fd.append("purposeOfVisit", form.purposeOfVisit);
        fd.append("email", form.email);
        form.fullNames.filter(Boolean).forEach(n => fd.append("fullNames", n));
      }
      idFiles.forEach(f => fd.append("identityProofs", f));

      const res  = await fetch(`${API}/guest-form/${token}`, { method: "POST", body: fd });
      const json = await readJson(res);
      if (!json.success) throw new Error(json.error);
      setSubmitted(true);
    } catch (e) { setError(e.message || "Submission failed. Please try again."); }
    finally { setSubmitting(false); }
  }

  async function payNow() {
    if (razorpayFailed) { setPayError(RAZORPAY_BLOCKED_MESSAGE); return; }
    if (!razorpayReady) { setPayError("Payment is still loading — please try again in a moment."); return; }
    setPayError(""); setPaying(true);
    try {
      const amount = booking.payment.remainingBalance;
      const orderRes = await fetch(`${API}/payment/create-order`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, amount }),
      });
      const orderJson = await orderRes.json();
      if (!orderJson.success) throw new Error(orderJson.error);

      const rzp = new window.Razorpay({
        key: orderJson.data.keyId,
        order_id: orderJson.data.orderId,
        amount: orderJson.data.amountPaise,
        currency: orderJson.data.currency,
        name: hotelName,
        description: `Balance for ${booking.bookingRef}`,
        handler: async (response) => {
          try {
            const verifyRes = await fetch(`${API}/payment/verify`, {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                token, amount,
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
              }),
            });
            const verifyJson = await verifyRes.json();
            if (!verifyJson.success) throw new Error(verifyJson.error);
            setBooking(b => ({ ...b, payment: {
              totalAmount: verifyJson.data.payment.totalAmount,
              advancePaid: verifyJson.data.payment.advancePaid,
              remainingBalance: Math.max(0, verifyJson.data.payment.totalAmount - verifyJson.data.payment.advancePaid),
              fullyPaid: verifyJson.data.payment.fullyPaid,
            } }));
          } catch (e) { setPayError(e.message || "Payment verification failed. Please contact the property if the amount was deducted."); }
          finally { setPaying(false); }
        },
        modal: { ondismiss: () => setPaying(false) },
        theme: { color: "#F59E0B" },
      });
      rzp.on("payment.failed", () => { setPayError("Payment failed. Please try again."); setPaying(false); });
      rzp.open();
    } catch (e) { setPayError(e.message || "Could not start payment."); setPaying(false); }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-10 text-center max-w-sm">
          <AlertTriangle size={36} className="text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Link Not Found</h2>
          <p className="text-sm text-gray-500">This guest registration link is invalid or has expired. Please contact the property.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 p-10 text-center max-w-sm w-full">
          <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500
            rounded-full flex items-center justify-center mx-auto mb-5 shadow-xl shadow-green-200">
            <Check size={38} className="text-white" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 mb-2">Thank You!</h2>
          <p className="text-sm text-gray-500 leading-relaxed">
            Your details have been recorded for booking <strong>{booking?.bookingRef}</strong>.
          </p>
          <p className="text-xs text-gray-400 mt-4">We look forward to hosting you at {hotelName}.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-orange-50/20">
      <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500
            flex items-center justify-center shadow-md">
            <Home size={17} className="text-white" />
          </div>
          <div>
            <p className="font-black text-gray-900 text-sm">{hotelName}</p>
            <p className="text-[10px] text-gray-400 uppercase tracking-widest">Guest Registration</p>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black text-gray-900 mb-1">Guest Details</h1>
          <p className="text-gray-500 text-sm">A few quick details to complete your check-in</p>
        </div>

        {booking && (
          <div className="bg-white rounded-2xl border border-amber-200 bg-amber-50/50 p-4 mb-5 flex items-center gap-3">
            <BedDouble size={18} className="text-amber-600 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-bold text-amber-800">{booking.bookingRef} · {booking.room?.name || "Room TBD"}</p>
              <p className="text-xs text-amber-600">
                {new Date(booking.checkIn).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                {" → "}
                {new Date(booking.checkOut).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                {" · Booked via "}{booking.source}
              </p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 md:p-8 space-y-5">
          {knownOnly ? (
            <div className="border border-green-200 bg-green-50/60 rounded-2xl p-4">
              <div className="flex items-start gap-2.5">
                <Check size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-green-800 uppercase tracking-wider mb-1.5">
                    We already have your details{booking?.source ? ` from ${booking.source}` : ""}
                  </p>
                  <p className="text-sm font-semibold text-gray-900 break-words">
                    {form.fullNames.filter(Boolean).join(", ") || "—"}
                  </p>
                  {form.whatsapp && <p className="text-xs text-gray-600 mt-0.5">{form.whatsapp}</p>}
                  {form.email && <p className="text-xs text-gray-600 break-all">{form.email}</p>}
                  <p className="text-xs text-gray-500 mt-2">
                    Just add your photo ID below and you're done.
                  </p>
                  <button type="button" onClick={() => setEditDetails(true)}
                    className="mt-1 -ml-1 px-1 min-h-[44px] inline-flex items-center gap-1.5
                      text-xs font-semibold text-amber-700 hover:text-amber-800">
                    <Pencil size={12} /> Something wrong? Update my details
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
              Number of Guests *
            </label>
            <select value={form.numberOfGuests}
              onChange={e => {
                const n = parseInt(e.target.value);
                set("numberOfGuests", n);
                set("fullNames", Array.from({ length: n }, (_, i) => form.fullNames[i] || ""));
              }}
              className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm bg-white
                focus:outline-none focus:ring-2 focus:ring-amber-400">
              {Array.from({ length: Math.max(8, guestTotal) }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n} Guest{n > 1 ? "s" : ""}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
              Full Name of All Guests (as per ID proof) *
            </label>
            {Array.from({ length: Math.max(1, form.numberOfGuests) }).map((_, i) => (
              <input key={i} type="text" value={form.fullNames[i] || ""}
                onChange={e => {
                  const names = [...form.fullNames];
                  names[i] = e.target.value;
                  set("fullNames", names);
                }}
                placeholder={`Guest ${i + 1} full name`}
                className={`w-full border border-gray-200 rounded-xl px-4 py-3 text-sm
                  focus:outline-none focus:ring-2 focus:ring-amber-400 ${i > 0 ? "mt-2" : ""}`} />
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                Travel From
              </label>
              <input type="text" value={form.travelFrom} onChange={e => set("travelFrom", e.target.value)}
                placeholder="City / Country"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm
                  focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                Travel To
              </label>
              <input type="text" value={form.travelTo} onChange={e => set("travelTo", e.target.value)}
                placeholder="Next destination"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm
                  focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
              Purpose of Visit
            </label>
            <input type="text" value={form.purposeOfVisit} onChange={e => set("purposeOfVisit", e.target.value)}
              placeholder="Leisure, business, family visit…"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm
                focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
              Booking Made Through
            </label>
            <input type="text" value={booking?.source || ""} disabled
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50 text-gray-500" />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
              WhatsApp Number *
            </label>
            <input type="tel" value={form.whatsapp} onChange={e => set("whatsapp", e.target.value)}
              placeholder="+91 98765 43210"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm
                focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
              Email <span className="normal-case font-normal text-gray-400">(optional)</span>
            </label>
            <input type="email" value={form.email} onChange={e => set("email", e.target.value)}
              placeholder="you@example.com"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm
                focus:outline-none focus:ring-2 focus:ring-amber-400" />
            <p className="text-xs text-gray-400 mt-1">We'll also email your booking updates and invoice here, if provided</p>
          </div>
            </>
          )}

          {booking?.payments?.enabled && booking?.payment?.remainingBalance > 0 && (
            <div className="border border-amber-200 bg-amber-50/60 rounded-2xl p-4 space-y-2">
              <p className="text-xs font-bold text-amber-800 uppercase tracking-wider">Pay Online</p>
              <p className="text-sm text-gray-700">
                Remaining balance: <strong>₹{booking.payment.remainingBalance.toLocaleString("en-IN")}</strong>
                {!booking.payments.payLaterEnabled && (
                  <span className="block text-xs text-amber-700 mt-1">
                    Online payment is required before you can submit your details.
                  </span>
                )}
              </p>
              <button onClick={payNow} disabled={paying}
                className="w-full py-3 rounded-xl bg-amber-500 text-white font-bold text-sm
                  hover:bg-amber-600 transition-all disabled:opacity-60">
                {paying ? "Opening payment…" : `Pay ₹${booking.payment.remainingBalance.toLocaleString("en-IN")} Now`}
              </button>
              {payError && <p className="text-xs text-red-600">{payError}</p>}
            </div>
          )}

          {booking?.payment?.fullyPaid && booking?.payment?.totalAmount > 0 && (
            <div className="border border-green-200 bg-green-50/60 rounded-2xl p-3 flex items-center gap-2">
              <Check size={15} className="text-green-600 flex-shrink-0" />
              <p className="text-xs text-green-700">Payment complete — thank you.</p>
            </div>
          )}


          {guestPolicyText && !booking?.policyAlreadyAccepted && (
            <div className="border border-gray-200 rounded-2xl p-4 space-y-3">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Guest Policy &amp; Terms and Conditions
              </p>
              <div className="max-h-40 overflow-y-auto text-xs text-gray-600 whitespace-pre-line
                bg-gray-50 rounded-xl p-3 leading-relaxed">
                {guestPolicyText}
              </div>
              <label className="flex items-start gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={policyAccepted}
                  onChange={e => setPolicyAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-amber-500 flex-shrink-0"
                />
                <span className="text-xs text-gray-700">
                  I have read and agree to the Guest Policy &amp; Terms and Conditions above.
                </span>
              </label>
            </div>
          )}

          {idProofUploadEnabled && (
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                Upload Identity Proof of All Guests {idsCovered ? "" : "*"}
              </label>
              <p className="text-xs text-gray-500 mb-2">
                One ID for each guest — {idsNeeded} needed
                {idFiles.length > 0 && <span className={idFiles.length >= idsNeeded ? "text-green-600" : "text-amber-700"}> · {idFiles.length} added</span>}
              </p>
              <label className="flex flex-col items-center justify-center border-2 border-dashed
                border-gray-200 rounded-2xl py-6 cursor-pointer hover:border-amber-400
                hover:bg-amber-50/30 transition-all group">
                <Upload size={26} className="text-gray-300 group-hover:text-amber-400 mb-2" />
                <span className="text-sm text-gray-400 group-hover:text-amber-600 font-medium">
                  Click to upload ID documents
                </span>
                <span className="text-xs text-gray-300 mt-1">
                  {idsCovered ? "Already on file — upload again only to replace it" : `Required · ${idsNeeded} ID${idsNeeded > 1 ? "s" : ""}`}
                  {" · up to 10 files · JPG, PNG, PDF · 10MB each"}
                </span>
                <input type="file" className="hidden" multiple accept=".jpg,.jpeg,.png,.pdf" onChange={addFile} />
              </label>

              {idFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {idFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
                      <Check size={14} className="text-green-500 flex-shrink-0" />
                      <span className="text-xs text-gray-700 flex-1 truncate">{f.name}</span>
                      <span className="text-xs text-gray-400">{(f.size / 1024 / 1024).toFixed(1)}MB</span>
                      <button onClick={() => removeFile(i)} className="p-1 hover:bg-gray-200 rounded-lg transition-colors">
                        <X size={12} className="text-gray-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl px-4 py-3 flex items-start gap-2">
              <AlertTriangle size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {(() => {
            const policyBlocked  = !!guestPolicyText && !booking?.policyAlreadyAccepted && !policyAccepted;
            const paymentBlocked = booking?.payments?.enabled && !booking.payments.payLaterEnabled
              && booking?.payment?.remainingBalance > 0 && !booking?.payment?.fullyPaid;
            const blockedTitle = policyBlocked
              ? "Please accept the Guest Policy & Terms and Conditions above"
              : paymentBlocked ? "Please complete the online payment above first" : undefined;
            return (
              <button onClick={submit}
                disabled={submitting || policyBlocked || paymentBlocked}
                title={blockedTitle}
                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500
                  text-white font-bold text-sm hover:from-amber-600 hover:to-orange-600
                  shadow-md transition-all disabled:opacity-60">
                {submitting ? "Submitting…" : "✓ Submit Details"}
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
